import React, { useContext, useEffect, useState } from "react";
import { useIdleTimer } from "react-idle-timer";
import Title from "../title/Title";
import { Routes, Route, useNavigate } from "react-router-dom";
import {
  Patient,
  Drugs,
  Diary,
  Symptoms,
  Measurements,
  LabTests,
  Patients,
  Page404,
  AppHeader,
  AppFooter,
} from "../pages";
import { InitProvider } from "../pages/InitContext";
import InitContext from "../pages/InitContext";
import useMmcService from "../../services/MmcService";
import Pdir from "../pages/Pdir";
import PatientInit from "../patientInit/PatientInit";
import ErrorMessage from "../errorMessage/ErrorMessage";
import PharmOracle from "../pages/PharmOracle";
import SessionTimeout from "./SessionTimout";
import StudyFeedbackPopup from "../pages/StudyFeedbackPopup";
import PGx from "../pgx/PGx";
import { Consts } from "../pages/consts";

const AppContent = () => {
  const { login, logout, updateContextPatient } = useContext(InitContext);
  const context = useContext(InitContext);

  const [initData, setInitData] = useState({
    token: "",
    role: "",
    patient: {},
    userId: -1,
    version: -1,
    description: "",
    patients: {},
  });

  const [patient, setPatient] = useState({});
  const navigate = useNavigate();
  const { loading, error, clearError, getDbUser } = useMmcService();
  const [showSessionTimeout, setShowSessionTimeout] = useState(false);
  const [hasLoggedOut, setHasLoggedOut] = useState(false);
  const SESSION_TIMEOUT_IN_MIN = 30;

  // redirect to log in if no token in session in any case (e.g. session timeout and browser refresh)
  useEffect(() => {
    if (!context?.token && !hasLoggedOut) {
      handleResetApp();
      setHasLoggedOut(true);
    }
  }, [logout, hasLoggedOut]);

  // prevent browser back after logout
  useEffect(() => {
    if (!context?.token) {
      navigate("/", { replace: true });
    }
  }, [context?.token, navigate]);

  const onIdle = () => {
    if (context?.token) {
      sessionStorage.removeItem("token");
      setShowSessionTimeout(true);
    }
  };

  const onActive = () => {
    // do nothing
  };

  useIdleTimer({
    timeout: 60000 * SESSION_TIMEOUT_IN_MIN,
    onIdle,
    onActive,
    debounce: 500,
  });

  const refreshPatient = (bearer, patId) => {
    getDbUser(bearer, patId).then((pat) => {
      if (pat) {
        setPatient(pat);

        login({
          ...initData,
          patient: pat,
          token: bearer,
          role: Consts.PATIENT,
          userId: patId,
        });

        navigate(pat?.registration_date ? "/drugsTab/" : "/patientInit");
      }
    });
  };

  function manageUserLogin(bearer, role, userId, username) {
    clearError();
    setShowSessionTimeout(false);

    if (role === Consts.PATIENT) {
      refreshPatient(bearer, userId);
    } else if (role?.startsWith(Consts.HCP) || role?.startsWith(Consts.STUDY)) {
      getDbUser(bearer, userId).then((patients) => {
        if (patients) {
          login({
            ...initData,
            token: bearer,
            role: role,
            userId: userId,
            patients: patients,
            username: username,
          });

          navigate("/patients/");
        }
      });
    }
  }

  function handleResetApp() {
    logout();
    navigate("/", { replace: true });
  }

  const handleFinishInit = (patient) => {
    setInitData({ ...initData, patient: patient });
    updateContextPatient(patient);
  };

  const handleCloseSessionTimeout = () => {
    setShowSessionTimeout(false);
    handleResetApp();
  };

  const errorMessage = error && <ErrorMessage message={`Error ${error} - please try again.`} />;

  return (
    <div>
      <AppHeader manageUserLogin={manageUserLogin} onResetApp={handleResetApp} loggedIn={context?.token}
                 registerAsPatient={context?.patient?.registration_date} />
      <main>
        <Routes>
          <Route path="/" element={<Title errorMessage={errorMessage} loading={loading} />} />
          <Route path="/patientInit/" element={ <PatientInit onFinishInit={handleFinishInit}
                                                             dbPatient={patient} onResetApp={handleResetApp}
                                                             onRefreshPatient={refreshPatient} /> } />
          <Route path="/patient/" element={<Patient patient={ patient } />} />
          <Route path="/pgx/" element={<PGx init={ false } printView={ false } onHandleBack={ null }
                                            onFinishInit={ null } onHandleNext={ null } patientId={patient?.id} />} />
          <Route path="/drugsTab/" element={<Drugs selectedPatient={ null } hcpView={ false } />} />
          <Route path="/diary/" element={<Diary />} />
          <Route path="/labValues/" element={<LabTests />} />
          <Route path="/symptoms/" element={<Symptoms />} />
          <Route path="/measurements/" element={<Measurements />} />
          <Route path="/patients/" element={ <Patients parentLoading={loading} parentErrorMessage={errorMessage} /> } />
          <Route path="/pdir/" element={ <Pdir studyBranch={patient?.study_id} userName={patient?.login} /> } />
          <Route path="/pharmaOracle/" element={ <PharmOracle studyBranch={patient?.study_id}
                                                              userName={patient?.login} /> } />
          <Route path="*" element={<Page404 />} />
        </Routes>
      </main>
      <AppFooter />
      {context?.token && showSessionTimeout && (
        <SessionTimeout showDialog={showSessionTimeout} onCloseDialog={handleCloseSessionTimeout} />
      )}
      {context?.patient?.registration_date && (
        <StudyFeedbackPopup patientRegistrationDate={context?.patient?.registration_date} userId={context?.userId} />
      )}
    </div>
  );
};

const App = () => {
  return (
    <InitProvider>
      <AppContent />
    </InitProvider>
  );
};

export default App;
